import { CustomApiHandler } from "@crescentParts/Store/CustomHandlerCreator";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { AttributeTypeFacetModel, BaseModel, CategoryFacetModel, ProductInventoryDto, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";

export interface RelatedProductLinesModel extends BaseModel {
    relatedProductLines: RelatedProductModel;
}

export interface RelatedProductModel extends BaseModel {
    pagination: PaginationModel;
    products: ProductModel[] | null;
    properties: { [key: string]: string };
    attributeTypeFacets: AttributeTypeFacetModel[] | null;
    categoryFacets: CategoryFacetModel[] | null;
}

type HandlerType = CustomApiHandler<{
    products?: any;
}>;

export const DispatchRelatedProducts: HandlerType = (props: any) => {
    props.dispatch({
        type: "Custom/LoadRelatedProducts",
        result: props.parameter,
    });
};

export const LoadRelatedProducts: HandlerType = (props: any) => {
    props.relatedProductLines = props.parameter.products!;
};

export const MapProducts: HandlerType = (props: any) => {
    props.productInfosByLineId = {};
    props.relatedProductLines?.forEach((o: any) => {
        props.productInfosByLineId[o.id] = {
            productId: o.id,
            qtyOrdered: o.qtyOrdered || 1,
            unitOfMeasure: o.unitOfMeasures[0].unitOfMeasure,
            productDetailPath: o.canonicalUrl,
        };
    });
};

export const LoadRealTimePrices: HandlerType = async (props: any) => {
    const { relatedProductLines, productInfosByLineId } = props;

    if (!relatedProductLines?.length) {
        return;
    }
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: relatedProductLines!.map((o: any) => ({
                productId: o.id,
                unitOfMeasure: o.unitOfMeasures[0].unitOfMeasure,
                qtyOrdered: o.qtyOrdered || 1,
            })),
            onComplete: realTimePricingProps => {
                if (realTimePricingProps?.apiResult) {
                    realTimePricingProps.apiResult.realTimePricingResults?.forEach(pricing => {
                        for (const relatedProductLineId in productInfosByLineId) {
                            const productInfo = productInfosByLineId[relatedProductLineId]!;
                            if (
                                productInfo.productId === pricing.productId &&
                                productInfo.unitOfMeasure === pricing.unitOfMeasure
                            ) {
                                productInfo.pricing = pricing;                                                         
                            }
                        }                        
                    });
                } else if (realTimePricingProps.error) {
                    for (const relatedProductLineId in productInfosByLineId) {
                        productInfosByLineId[relatedProductLineId]!.failedToLoadPricing = true;
                    }
                }
                props.pricingLoaded = true; 
                props.realTimePricing = realTimePricingProps;                  
            },            
        }),
    );

    await waitFor(() => !!props.pricingLoaded);
};

export const LoadRealTimeInventory: HandlerType = async (props: any) => {
    const { relatedProductLines, productInfosByLineId } = props;

    if (!relatedProductLines?.length) {
        return;
    }
    props.dispatch(
        loadRealTimeInventory({
            productIds: relatedProductLines.map((product: any) => product.id),
            onComplete: realTimeInventory => {
                if (realTimeInventory?.apiResult) {
                    realTimeInventory?.apiResult.realTimeInventoryResults?.forEach((inventory: ProductInventoryDto) => {
                        const productInfo = productInfosByLineId[inventory.productId];
                        if (productInfo) {
                            productInfo.inventory = inventory;
                        }
                    });

                }
                props.inventoryLoaded = true; 
                                
            },
            onError: () => {
                props.dispatch({
                    type: "Custom/RelatedProducts/FailedLoadRealTimeInventory"
                });
            },           
        }),
    );

   // await waitFor(() => !!props.inventoryLoaded);
};


export const DispatchCompleteLoadRelatedProducts: HandlerType = props => {
    props.dispatch({
        type: "Custom/DispatchCompleteLoadRelatedProducts",
        relatedProductLines: props.parameter.products,
    });
};

export const DispatchCompleteLoadPricing: HandlerType = props => {
    props.dispatch({
        type: "Custom/RelatedProducts/SetRealtimePricingAndInventory",
        ahriRelatedProductInfosByLineId: props,
    });
};

export const chain = [
    DispatchRelatedProducts,
    LoadRelatedProducts,
    MapProducts,
    LoadRealTimePrices,
    LoadRealTimeInventory,
    DispatchCompleteLoadRelatedProducts,
    DispatchCompleteLoadPricing,
];

const loadAHRIRelatedProducts = createHandlerChainRunner(chain, "loadAHRIRelatedProducts");
export default loadAHRIRelatedProducts;